import { useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { I18nContext } from 'react-i18next';

import { LocalStorage } from './services';

import ProtectedRoute from './pages/ProtectedRoute/ProtectedRoute.page';
import AppLayout from './pages/AppLayout/AppLayout.page';
import Login from './pages/Authentication/Login/Login.page';
import Dashboard from './pages/General/Dashboard/Dashboard.page';
import AdminsList from './pages/Admin/AdminsList/AdminsList.page';
import AdminsCreate from './pages/Admin/AdminsCreate/AdminsCreate.page';
import TadbirContents from './pages/Admin/TadbirContents/TadbirContents.page';
import UserCategoriesList from './pages/User/UserCategoriesList/UserCategoriesList.page';
import UserCategoriesCreate from './pages/User/UserCategoriesCreate/UserCategoriesCreate.page';
import UsersList from './pages/User/UsersList/UsersList.page';
import UsersCreate from './pages/User/UsersCreate/UsersCreate.page';
import UsersEdit from './pages/User/UsersEdit/UsersEdit.page';
import CreditsList from './pages/User/CreditsList/CreditsList.page';
import CreditsCreateByExcel from './pages/User/CreditsCreateByExcel/CreditsCreateByExcel.page';
import MarketingCodesList from './pages/User/MarketingCodesList/MarketingCodesList.page';
import MarketingCodesCreate from './pages/User/MarketingCodesCreate/MarketingCodesCreate.page';
import ReferralChangesList from './pages/User/ReferralChangesList/ReferralChangesList.page';
import RequestCategoriesList from './pages/Portal/RequestCategoriesList/RequestCategoriesList.page';
import RequestCategoriesCreate from './pages/Portal/RequestCategoriesCreate/RequestCategoriesCreate.page';
import RequestsList from './pages/Portal/RequestsList/RequestsList.page';
import RequestsCreate from './pages/Portal/RequestsCreate/RequestsCreate.page';
import MarketingContents from './pages/Portal/MarketingContents/MarketingContents.page';
import StocksList from './pages/Analysis/StocksList/StocksList.page';
import SignalsList from './pages/Analysis/SignalsList/SignalsList.page';
import SignalsCreate from './pages/Analysis/SignalsCreate/SignalsCreate.page';
import SignalCommentsList from './pages/Analysis/SignalCommentsList/SignalCommentsList.page';
import SignalCommentsCreate from './pages/Analysis/SignalCommentsCreate/SignalCommentsCreate.page';
import ConsultantsList from './pages/Consultation/ConsultantsList/ConsultantsList.page';
import ConsultantsCreate from './pages/Consultation/ConsultantsCreate/ConsultantsCreate.page';
import ConsultationCategoriesList from './pages/Consultation/ConsultationCategoriesList/ConsultationCategoriesList.page';
import ConsultationCategoriesCreate from './pages/Consultation/ConsultationCategoriesCreate/ConsultationCategoriesCreate.page';
import ConsultationsList from './pages/Consultation/ConsultationsList/ConsultationsList.page';
import ConsultationsCreate from './pages/Consultation/ConsultationsCreate/ConsultationsCreate.page';
import ProductCategoriesList from './pages/Shop/ProductCategoriesList/ProductCategoriesList.page';
import ProductCategoriesCreate from './pages/Shop/ProductCategoriesCreate/ProductCategoriesCreate.page';
import ProductsList from './pages/Shop/ProductsList/ProductsList.page';
import ProductsCreate from './pages/Shop/ProductsCreate/ProductsCreate.page';
import ProductOrdersList from './pages/Shop/ProductOrdersList/ProductOrdersList.page';
import ProductOrdersCreate from './pages/Shop/ProductOrdersCreate/ProductOrdersCreate.page';
import ContestsList from './pages/Contests/ContestsList/ContestsList.page';
import ContestForecastsList from './pages/Contests/ContestForecastsList/ContestForecastsList.page';
import ContestBestsList from './pages/Contests/ContestBestsList/ContestBestsList.page';
import BannersList from './pages/Banners/BannersList/BannersList.page';
import BannersCreate from './pages/Banners/BannersCreate/BannersCreate.page';
import TransactionCategoriesList from './pages/Financial/TransactionCategoriesList/TransactionCategoriesList.page';
import TransactionCategoriesCreate from './pages/Financial/TransactionCategoriesCreate/TransactionCategoriesCreate.page';
import TransactionsList from './pages/Financial/TransactionsList/TransactionsList.page';
import TransactionsCreate from './pages/Financial/TransactionsCreate/TransactionsCreate.page';

const App = () => {
	const i18nContext = useContext(I18nContext);
	const location = useLocation();

	let isLoggedIn = LocalStorage.get('token') && LocalStorage.get('verify-login');
	useEffect(() => {
		isLoggedIn = LocalStorage.get('token') && !!LocalStorage.get('verify-login');
	}, [location]);

	const [darkTheme] = useState((LocalStorage.get('theme') && LocalStorage.get('theme') === 'dark') ? true : false);
	const [faLanguage] = useState((LocalStorage.get('language') && LocalStorage.get('language') === 'en') ? false : true);
	useEffect(() => {
		document.querySelector('html').setAttribute('data-bs-theme', darkTheme ? 'dark' : 'light');
		LocalStorage.set('theme', darkTheme ? 'dark' : 'light');
		document.querySelector('html').setAttribute('dir', faLanguage ? 'rtl' : 'ltr');
		LocalStorage.set('language', faLanguage ? 'fa' : 'en');
		const themeStylesLink = document.getElementById('theme-styles');
		const themeStylesFile = faLanguage ? themeStylesLink.getAttribute('data-fa-url') : themeStylesLink.getAttribute('data-en-url');
		themeStylesLink.href = themeStylesFile;
		i18nContext.i18n.changeLanguage(faLanguage ? 'fa' : 'en');
	}, []);

	return (
		<Routes>
			<Route element={<ProtectedRoute isLoggedIn={isLoggedIn}><AppLayout /></ProtectedRoute>}>
				<Route index element={<Navigate replace to="/dashboard"/>}/>
				<Route path="/dashboard" element={<Dashboard />}/>
				<Route path="/admins-create" element={<AdminsCreate />}/>
				<Route path="/admins-list" element={<AdminsList />}/>
				<Route path="/admins-edit/:id" element={<AdminsCreate componentType='edit' />}/>
				<Route path="/tadbir-contents" element={<TadbirContents />}/>
				<Route path="/user-categories-create" element={<UserCategoriesCreate />}/>
				<Route path="/user-categories-list" element={<UserCategoriesList />}/>
				<Route path="/user-categories-edit/:id" element={<UserCategoriesCreate componentType='edit' />}/>
				<Route path="/users-create" element={<UsersCreate />}/>
				<Route path="/users-list" element={<UsersList />}/>
				<Route path="/users-edit/:id" element={<UsersEdit />}/>
				<Route path="/credits-list" element={<CreditsList />}/>
				<Route path="/credits-create-by-excel" element={<CreditsCreateByExcel />}/>
				<Route path="/marketing-codes-list" element={<MarketingCodesList />}/>
				<Route path="/marketing-codes-edit/:id" element={<MarketingCodesCreate componentType='edit' />}/>
				<Route path="/referral-changes-list" element={<ReferralChangesList />}/>
				<Route path="/stocks-list" element={<StocksList />}/>
				<Route path="/signals-list" element={<SignalsList />}/>
				<Route path="/signals-edit/:id" element={<SignalsCreate componentType='edit' />}/>
				<Route path="/signal-comments-list" element={<SignalCommentsList />}/>
				<Route path="/signal-comments-edit/:id" element={<SignalCommentsCreate componentType='edit' />}/>
				<Route path="/request-categories-create" element={<RequestCategoriesCreate />}/>
				<Route path="/request-categories-list" element={<RequestCategoriesList />}/>
				<Route path="/request-categories-edit/:id" element={<RequestCategoriesCreate componentType='edit' />}/>
				<Route path="/requests-list" element={<RequestsList />}/>
				<Route path="/requests-edit/:id" element={<RequestsCreate componentType='edit' />}/>
				<Route path="/marketing-contents" element={<MarketingContents />}/>
				<Route path="/consultants-create" element={<ConsultantsCreate />}/>
				<Route path="/consultants-list" element={<ConsultantsList />}/>
				<Route path="/consultants-edit/:id" element={<ConsultantsCreate componentType='edit' />}/>
				<Route path="/consultation-categories-create" element={<ConsultationCategoriesCreate />}/>
				<Route path="/consultation-categories-list" element={<ConsultationCategoriesList />}/>
				<Route path="/consultation-categories-edit/:id" element={<ConsultationCategoriesCreate componentType='edit' />}/>
				<Route path="/consultations-list" element={<ConsultationsList />}/>
				<Route path="/consultations-edit/:id" element={<ConsultationsCreate componentType='edit' />}/>
				<Route path="/product-categories-create" element={<ProductCategoriesCreate />}/>
				<Route path="/product-categories-list" element={<ProductCategoriesList />}/>
				<Route path="/product-categories-edit/:id" element={<ProductCategoriesCreate componentType='edit' />}/>
				<Route path="/products-create" element={<ProductsCreate />}/>
				<Route path="/products-list" element={<ProductsList />}/>
				<Route path="/products-edit/:id" element={<ProductsCreate componentType='edit' />}/>
				<Route path="/product-orders-list" element={<ProductOrdersList />}/>
				<Route path="/product-orders-edit/:id" element={<ProductOrdersCreate componentType='edit' />}/>
				<Route path="/contests-list" element={<ContestsList />}/>
				<Route path="/contest-forecasts-list" element={<ContestForecastsList />}/>
				<Route path="/contest-bests-list" element={<ContestBestsList />}/>
				<Route path="/banners-create" element={<BannersCreate />}/>
				<Route path="/banners-list" element={<BannersList />}/>
				<Route path="/banners-edit/:id" element={<BannersCreate componentType='edit' />}/>
				<Route path="/transaction-categories-create" element={<TransactionCategoriesCreate />}/>
				<Route path="/transaction-categories-list" element={<TransactionCategoriesList />}/>
				<Route path="/transaction-categories-edit/:id" element={<TransactionCategoriesCreate componentType='edit' />}/>
				<Route path="/transactions-create" element={<TransactionsCreate />}/>
				<Route path="/transactions-list" element={<TransactionsList />}/>
				<Route path="/*" element={<Dashboard />}/>
			</Route>
			<Route path="/login" element={isLoggedIn ? <Navigate replace to="/dashboard"/> : <Login />}></Route>
		</Routes>
	);
};

export default App;
